import React, { useState, useEffect, useRef } from "react";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";
import { getColorClassName } from "@qlik/qlik/src/lib/util/colors";
import { isFullyQualifiedUrl, scrollToElement, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import { normalizePath } from "@qlik/utils/normalizePathOrUrl";
import * as styles from "./styles.module.css";
/**
 * Function to handle click on a letter.
 * Scrolls to the section with the given id.
 * @param {string} id - The id of the section to scroll to.
 */

/**
 * Component for inner page navigation.
 * @param {Object} data - The data for the navigation items.
 */
const handleStickyNav = (cards, cardId, urlId, refs) => {
    const section = document.getElementById(urlId);
    scrollToElement({ current: section });

    setTimeout(() => {
        cards?.cta?.forEach((cta) => {
            const ctaRef = refs[cta.id];
            if (ctaRef && ctaRef.current) {
                ctaRef.current.classList.remove("active");
            }
        });
        const cardRef = refs[cardId];
        if (cardRef && cardRef.current) {
            cardRef.current.classList.add("active");
        }
    }, 400);
};

const PageInnerNavigation = ({ data }) => {
    const { cardItem, backgroundColor, customId } = data ?? {};
    const innerNav = useRef();
    const [stickNav, setStickNav] = useState(false);
    const { headerHeight } = useAppContext();
    let screenMargin = "-260px -50% -65%";

    // Create a ref for each card
    const cardRefs = data?.cardItem?.cta?.reduce((acc, card) => {
        acc[card.id] = useRef();
        return acc;
    }, {});

    /**
     * Function to handle scroll event.
     * Sets the active section and whether the navigation should stick.
     */
    const handleScroll = () => {
        const pageYOffset = window.scrollY;
        const innerNavCurrent = innerNav.current;
        const notificationBar = document.querySelectorAll("[data-notification]");

        setStickNav(innerNavCurrent.offsetTop <= pageYOffset + headerHeight + 50);

        notificationBar[0]?.classList.toggle("hidden", innerNavCurrent.offsetTop <= pageYOffset + headerHeight + 40);
    };

    const getScreenMargin = () => {
        if (window.innerWidth > 1980) {
            screenMargin = "-420px -50% -65%";
        }
    };

    // Add scroll event listener on mount and remove on unmount
    useEffect(() => {
        // handle scroll position on mount
        handleScroll();
        getScreenMargin();

        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", getScreenMargin);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", getScreenMargin);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headerHeight]);

    const observer = useRef(null);

    useEffect(() => {
        const options = {
            root: null,
            rootMargin: screenMargin,
            threshold: 0,
        };

        const sections = document.querySelectorAll(
            ".inner-page-nav ~ [data-section], .inner-page-nav ~ section [data-section]"
        );

        observer.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    // Select all elements with the class ‘target-class’
                    const elements = document.querySelectorAll(".inner-nav li");
                    // Loop through the NodeList and remove the class from each element
                    elements.forEach((element) => {
                        element.classList.remove("active");
                    });
                    document
                        .querySelector(
                            `.inner-nav li a[href="${
                                !isFullyQualifiedUrl(entry.target.id) ? `#${entry.target.id}` : null
                            }"]`
                        )
                        ?.parentElement?.classList.add("active");
                }
                if (
                    (sections[sections.length - 1]?.id === entry.target.id || sections[0]?.id === entry.target.id) &&
                    !entry.isIntersecting
                ) {
                    // Select all elements with the class ‘target-class’
                    document
                        .querySelector(
                            `.inner-nav li a[href="${
                                !isFullyQualifiedUrl(entry.target.id) ? `#${entry.target.id}` : null
                            }"]`
                        )
                        ?.parentElement?.classList.remove("active");
                }
            });
        }, options);
        sections.forEach((section) => {
            const sectionEl = document.getElementById(section.id);
            if (sectionEl) {
                observer.current.observe(sectionEl);
            }
        });
        return () => {
            sections.forEach((section) => {
                const sectionEl = document.getElementById(section.id);
                if (sectionEl) {
                    observer.current.unobserve(sectionEl);
                }
            });
        };
    }, [screenMargin]);

    const activeStyle = {
        top: headerHeight,
    };

    useEffect(() => {
        const onloadPageScroll = () => {
            const urlHash = window.location.hash;
            const hashValue = urlHash.replace(/^#/, "");
            const section = document.getElementById(hashValue);
            scrollToElement({ current: section });
        };
        onloadPageScroll();
    }, []);

    const { sectionColorMapping } = getColorClassName(backgroundColor);

    return (
        <section
            ref={innerNav}
            className={`inner-page-nav overflow-hidden hidden bg-stone-light lg:block pt-1.75 transition-all
        ${stickNav ? "stick-nav" : ""}  ${sectionColorMapping.bgColor}`}
            style={stickNav ? activeStyle : {}}
            id={customId}
        >
            <div className="container">
                <div className={`pb-5.5 ${stickNav ? "!pb-2.5" : ""} inner-nav-wrapper bg-stone-light`}>
                    <ul className="inner-nav flex-wrap lg:flex-nowrap flex list-none flex-row p-0 m-0 items-end gap-7.5">
                        {cardItem?.cta?.map((card) => (
                            <li
                                ref={cardRefs[card.id]} // Assign the ref to the li element
                                id={card.id}
                                key={card.id}
                                className={`card-id${card.id} navItem flex-1 px-0 ml-0 mx-0 after:hidden min-w-[210px] max-w-[210px] lg:min-w-0 lg:max-w-full ${styles.navItem}`}
                            >
                                <a
                                    className={`block py-3.75 text-gray hover:text-green tracking-normal text-center text-base font-semibold leading-snug ${
                                        stickNav ? "pt-0 pb-2.5" : ""
                                    }`}
                                    href={
                                        card?.externalUrl && !isFullyQualifiedUrl(card?.externalUrl)
                                            ? `#${card?.externalUrl}`
                                            : card?.externalUrl || normalizePath(card)
                                    }
                                    rel={card?.browsingContext === "New Tab" ? "noreferrer" : null}
                                    target={card?.browsingContext === "New Tab" ? "_blank" : "_self"}
                                    onClick={
                                        card?.externalUrl && !isFullyQualifiedUrl(card?.externalUrl)
                                            ? (e) => {
                                                  e.preventDefault();
                                                  handleStickyNav(
                                                      cardItem,
                                                      card.id,
                                                      card.externalUrl,
                                                      cardRefs[card.id]
                                                  );
                                              }
                                            : undefined
                                    }
                                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(card.text) }}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default PageInnerNavigation;
